import type { MouseEvent, ReactNode } from "react";
import type { CalendarDate } from "@internationalized/date";
import { VisuallyHidden } from "../visually-hidden";
import { longDateFormat, toDateString } from "../date-picker/date-input";
import { cn } from "../../utilities/tailwind";

export type DayRenderProperties = {
  date?: CalendarDate | undefined;
  month: CalendarDate;
  rangeStart?: CalendarDate | undefined;
  rangeEnd?: CalendarDate | undefined;
  isPadding?: boolean | undefined;
  isActive?: boolean | undefined;
  isRangeStart?: boolean | undefined;
  isRangeEnd?: boolean | undefined;
  isInRange?: boolean | undefined;
  isDisabled?: boolean | undefined;
  highlightRange?: boolean | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
};
export type RenderDate = (properties: DayRenderProperties) => ReactNode;
export type DayProperties = DayRenderProperties & {
  renderDate?: RenderDate | undefined;
};
export const Day = ({ renderDate, ...properties }: DayProperties) => {
  const {
    date,
    isPadding,
    highlightRange,
    isRangeStart,
    isRangeEnd,
    className,
    isInRange,
    isActive,
    isDisabled,
    rangeStart,
    rangeEnd,
    onClick,
  } = properties;

  // Hierarchy: renderDate content -> numeric day fallback -> empty.
  const content = renderDate?.(properties) ?? date?.day ?? null;

  // to do: Clean this up to make it a bit easier to understand how we're getting these things as a consumer
  const Component = onClick && !isPadding ? "button" : "span";
  const handleClick = onClick
    ? (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onClick();
      }
    : undefined;

  const showRangeFill =
    Boolean(highlightRange) &&
    Boolean(rangeStart && rangeEnd) &&
    (Boolean(isRangeStart) || Boolean(isRangeEnd));

  return (
    <div
      className={cn(`relative data-[in-range=true]:bg-off-white`, className)}
      data-in-range={isInRange}
    >
      {showRangeFill ? (
        <div
          className="absolute inset-y-0 start-1/2 end-0 bg-off-white data-[is-range-end=true]:start-0 data-[is-range-end=true]:end-1/2"
          data-is-range-end={isRangeEnd}
        />
      ) : null}
      <Component
        className="relative mx-auto flex aspect-square size-11 items-center justify-center rounded-full border-2 border-transparent text-royal-blue enabled:hover:border-royal-blue data-[active=true]:bg-royal-blue data-[active=true]:text-white data-[disabled=true]:text-medium-gray data-[in-range=true]:bg-transparent"
        data-active={isActive}
        data-disabled={isDisabled}
        data-in-range={isInRange}
        disabled={isDisabled}
        onMouseDown={handleClick}
        tabIndex={-1}
        type="button"
      >
        <span aria-hidden="true">{content}</span>
        <VisuallyHidden>{toDateString(date, longDateFormat)}</VisuallyHidden>
      </Component>
    </div>
  );
};
Day.displayName = "Calendar.Day";
